import { useCallback, useEffect, useMemo, useState } from "react";
import { NotificationEmptyState } from "./NotificationEmptyState";
import { getNotifications, markAsRead, markAsReadAll } from "../api/NotificationApi";
import { formatDistanceToNow } from "date-fns";
import { renderItemIcon } from "../main/component/LinkItemPickerV2";
import Editor from "../components/Editor";
import { navigateToItem } from "../Navigator";
import { fromJS } from "immutable";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BellRing, Bot, CheckCircle2 } from "lucide-react";
import {
  isPushSupported,
  getNotificationPermission,
  enableBrowserNotifications,
  setOneSignalUserId,
} from "./pushNotificationUtils";

/* ── Types ── */

interface Notification {
  id: string;
  message: string;
  type: string;
  timestamp: string;
  read: boolean;
  author: any;
  item: any;
  activity: any;
  linkWorkspaceId?: string;
  linkRuleId?: string;
}

type TabId = "all" | "unread";

/* ── Custom Hook ── */

function useNotifications(userId: string) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(() => {
    if (userId) {
      getNotifications(userId).then((data) => {
        setNotifications(data);
        setLoading(false);
      });
    }
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 50000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (userId) setOneSignalUserId(userId);
  }, [userId]);

  const handleMarkAsRead = useCallback(
    async (notification: Notification) => {
      if (!notification.read) {
        await markAsRead(userId, notification.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
        );
      }
    },
    [userId]
  );

  const handleMarkAllAsRead = useCallback(async () => {
    await markAsReadAll(userId);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, [userId]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  return { notifications, unreadCount, loading, handleMarkAsRead, handleMarkAllAsRead };
}

function usePushPermission(userId: string) {
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState<NotificationPermission | null>(null);
  const [pushEnabling, setPushEnabling] = useState(false);

  useEffect(() => {
    isPushSupported().then(setPushSupported);
    getNotificationPermission().then(setPushPermission);
  }, []);

  const handleEnable = useCallback(async () => {
    setPushEnabling(true);
    try {
      const ok = await enableBrowserNotifications(userId);
      if (ok) {
        setPushPermission("granted");
      } else {
        getNotificationPermission().then(setPushPermission);
      }
    } finally {
      setPushEnabling(false);
    }
  }, [userId]);

  return { pushSupported, pushPermission, pushEnabling, handleEnable };
}

/* ── Sub-components ── */

/* ── Notification Item ── */
function NotificationItem({
  notification,
  onMarkAsRead,
  onNavigate,
}: {
  notification: Notification;
  onMarkAsRead: (n: Notification) => void;
  onNavigate: (n: Notification) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const description = notification.activity?.event?.data?.description || "";
  const hasItem = notification.item && notification.item.data?.["name"]?.value;
  const hasAutomationLink =
    notification.type === "AUTOMATION" &&
    Boolean(notification.linkWorkspaceId) &&
    Boolean(notification.linkRuleId);
  const hasNavigationTarget = Boolean(hasItem || hasAutomationLink);

  // Truncate long messages (e.g. automation errors)
  const maxLen = 80;
  const rawMessage = notification.message || "";
  const isTruncated = rawMessage.length > maxLen;
  const displayMessage = isTruncated && !expanded ? rawMessage.slice(0, maxLen) + "…" : rawMessage;

  const relativeTime = (() => {
    try {
      return formatDistanceToNow(new Date(parseFloat(notification.timestamp) * 1000), {
        addSuffix: true,
      });
    } catch {
      return "";
    }
  })();

  return (
    <button
      type="button"
      className={`w-full text-left px-4 py-3 flex gap-3 transition-colors hover:bg-gray-50 ${
        !notification.read ? "bg-blue-50/30" : ""
      }`}
      onClick={() => {
        onMarkAsRead(notification);
        if (hasNavigationTarget) onNavigate(notification);
      }}
    >
      {/* Unread indicator */}
      <div className="flex flex-col items-center pt-2 w-2 shrink-0">
        {!notification.read && <span className="w-2 h-2 rounded-full bg-blue-500" />}
      </div>

      {/* Avatar */}
      <div className="shrink-0">
        {notification.author?.avatarUrl ? (
          <img
            alt=""
            src={notification.author.avatarUrl}
            className="w-8 h-8 rounded-full bg-gray-200 object-cover"
          />
        ) : notification.author?.name ? (
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-semibold text-blue-600">
            {notification.author.name[0].toUpperCase()}
          </div>
        ) : rawMessage.toLowerCase().includes("automation") ? (
          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
            <Bot className="w-4 h-4 text-purple-600" />
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <BellRing className="w-4 h-4 text-gray-500" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className={expanded ? "" : "flex items-baseline gap-2"}>
          <p
            className={`text-sm text-gray-900 flex-1 ${expanded ? "whitespace-pre-wrap break-words" : "truncate"}`}
          >
            {displayMessage}
          </p>
          {!expanded && (
            <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">{relativeTime}</span>
          )}
        </div>
        {expanded && <span className="text-xs text-gray-400 mt-0.5 block">{relativeTime}</span>}

        {isTruncated && (
          <button
            type="button"
            className="text-xs text-blue-500 hover:text-blue-700 mt-0.5"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded ? t("Show less") : t("Show more")}
          </button>
        )}

        {hasItem && (
          <p className="mt-0.5 text-xs text-gray-500 flex items-center gap-1 truncate">
            {renderItemIcon(notification.item)}
            <span className="truncate">{notification.item.data?.["name"]?.value}</span>
          </p>
        )}

        {description && (
          <div className="mt-1 text-xs text-gray-600 line-clamp-2">
            <Editor
              readOnly={true}
              viewMode={true}
              editorContent={{ htmlContent: description, contents: {} }}
            />
          </div>
        )}
      </div>
    </button>
  );
}

function NotificationSkeleton() {
  return (
    <div className="divide-y divide-gray-100">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="px-4 py-3 flex gap-3 animate-pulse">
          <div className="w-2 shrink-0 pt-2">
            <div className="w-2 h-2 rounded-full bg-gray-200" />
          </div>
          <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />
          <div className="flex-1 min-w-0 space-y-2 py-0.5">
            <div className="flex items-center gap-3">
              <div className="h-3.5 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-100 rounded w-16 shrink-0" />
            </div>
            <div className="h-3 bg-gray-100 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Main Component ── */

export default function Notifications({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId }: any = useParams();
  const [activeTab, setActiveTab] = useState<TabId>("unread");

  const { notifications, unreadCount, loading, handleMarkAsRead, handleMarkAllAsRead } =
    useNotifications(userId);
  const { pushSupported, pushPermission, pushEnabling, handleEnable } = usePushPermission(userId);

  const filteredNotifications = useMemo(
    () => (activeTab === "unread" ? notifications.filter((n) => !n.read) : notifications),
    [notifications, activeTab]
  );

  const handleNavigateToItem = useCallback(
    (notification: Notification) => {
      if (notification.item) {
        navigateToItem({ item: fromJS(notification.item), navigate });
        return;
      }
      if (
        notification.type === "AUTOMATION" &&
        notification.linkWorkspaceId &&
        notification.linkRuleId
      ) {
        navigate(
          `/workspaces/${notification.linkWorkspaceId}/automations/${notification.linkRuleId}`
        );
      }
    },
    [navigate]
  );

  const tabs: { id: TabId; label: string; count: number }[] = [
    { id: "all", label: t("All"), count: notifications.length },
    { id: "unread", label: t("Unread"), count: unreadCount },
  ];

  return (
    <div
      className="w-[380px] max-h-[calc(100dvh-80px)] flex flex-col bg-white border border-gray-200 rounded-2xl overflow-hidden z-10"
      style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)" }}
    >
      {/* ── Header ── */}
      <div className="shrink-0 px-4 pt-4 pb-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900">{t("Notifications")}</h2>
          <div className="flex items-center gap-3">
            {pushSupported &&
              pushPermission !== "granted" &&
              (pushPermission === "denied" ? (
                <span
                  className="text-xs text-gray-400"
                  title={t("Enable notifications in your browser's site settings.")}
                >
                  {t("Notifications blocked")}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleEnable}
                  disabled={pushEnabling}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                >
                  {pushEnabling ? t("Enabling…") : t("Enable browser notifications")}
                </button>
              ))}
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {t("Mark all read")}
              </button>
            )}
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex gap-1 border-b border-gray-100 -mx-4 px-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? "text-blue-600 border-blue-600"
                  : "text-gray-500 border-transparent hover:text-gray-700"
              }`}
            >
              {tab.label}
              <span
                className={`ml-1.5 text-xs ${
                  activeTab === tab.id ? "text-blue-500" : "text-gray-400"
                }`}
              >
                ({tab.count})
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Notification list ── */}
      <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100">
        {loading ? (
          <NotificationSkeleton />
        ) : filteredNotifications.length > 0 ? (
          filteredNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              onNavigate={handleNavigateToItem}
            />
          ))
        ) : (
          <NotificationEmptyState />
        )}
      </div>

      {/* ── Footer ── */}
      <div className="shrink-0 border-t border-gray-100 px-4 py-3 flex justify-center">
        <button
          type="button"
          onClick={() =>
            navigate(
              `/workspaces/${projectId}/settings/user?item=notifications&key=User%20Settings`
            )
          }
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm text-gray-700 font-medium border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
        >
          {t("Notification Settings")}
        </button>
      </div>
    </div>
  );
}
